(() => {
  'use strict';

  const SELECTOR = '[data-faj-product-finder]';
  const GLOBAL_KEY = 'FAJProductFinder';

  const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normalizeImage(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  }

  function getHandle(product) {
    if (product?.handle) return product.handle;
    const url = product?.url || product?.online_store_url || '';
    return url.match(/\/products\/([^/?#]+)/)?.[1] || '';
  }

  function parseConfig(root) {
    const node = root.querySelector('[data-pf-config]');
    if (!node) return {};

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error('[FAJ Product Finder] Invalid configuration JSON.', error);
      return {};
    }
  }

  function initProductFinder(root) {
    if (!root || root.dataset.pfInitialized === 'true') return;

    const input = root.querySelector('[data-pf-search-input]');
    const category = root.querySelector('[data-pf-category]');
    const submit = root.querySelector('[data-pf-submit]');
    const grid = root.querySelector('[data-pf-grid]');
    const count = root.querySelector('[data-pf-count]');
    const state = root.querySelector('[data-pf-state]');
    const pills = Array.from(root.querySelectorAll('[data-pf-filter]'));
    const mainWhatsapp = root.querySelector('[data-pf-main-whatsapp]');
    const viewMoreWrap = root.querySelector('[data-pf-view-more-wrap]');
    const viewMore = root.querySelector('[data-pf-view-more]');
    const config = parseConfig(root);

    if (!input || !category || !submit || !grid || !count || !state) {
      console.warn('[FAJ Product Finder] Required elements are missing.', root);
      return;
    }

    root.dataset.pfInitialized = 'true';

    Object.assign(config, {
      limit: Number(config.limit || 6),
      defaultQuery: config.defaultQuery || '',
      whatsappNumber: config.whatsappNumber || '',
      whatsappText: config.whatsappText || 'Check out this product:',
      quoteButtonText: config.quoteButtonText || 'Get a Quote',
      addButtonText: config.addButtonText || 'Add to Cart',
      addedButtonText: config.addedButtonText || 'Added',
      moneyFormat: config.moneyFormat || '$0.00',
      currency: config.currency || '',
      showDefaultResults: Boolean(config.showDefaultResults),
      showViewMore: Boolean(config.showViewMore),
      openCartAfterAdd: Boolean(config.openCartAfterAdd),
      rootUrl: config.rootUrl || '/'
    });

    const normalizedRoot = `${String(config.rootUrl).replace(/\/+$/, '')}/`;
    let activeFilter = '';
    let debounceTimer = null;
    let activeController = null;
    const pendingAddButtons = new WeakSet();

    function updateCount(number) {
      count.textContent = `${number} ${number === 1 ? 'result' : 'results'}`;
    }

    function showState(message) {
      grid.innerHTML = '';
      state.textContent = message;
      state.classList.add('is-visible');
      updateCount(0);
    }

    function hideState() {
      state.classList.remove('is-visible');
    }

    function showLoading() {
      hideState();
      updateCount(0);
      grid.innerHTML = Array.from({ length: Math.min(config.limit, 4) })
        .map(() => '<div class="pf-skeleton" aria-hidden="true"></div>')
        .join('');
    }

    function getSearchQuery() {
      const pieces = [
        cleanText(input.value),
        cleanText(category.value),
        activeFilter
      ].filter(Boolean);

      return cleanText(pieces.join(' ')) || cleanText(config.defaultQuery);
    }

    function updateViewMore(query, visible = true) {
      if (!viewMoreWrap || !viewMore || !config.showViewMore) return;

      const url = new URL(`${normalizedRoot}search`, window.location.origin);
      const cleanQuery = cleanText(query);

      if (cleanQuery) url.searchParams.set('q', cleanQuery);
      url.searchParams.set('type', 'product');

      viewMore.href = `${url.pathname}${url.search}`;
      viewMoreWrap.hidden = !visible;
    }

    function formatMoney(amount) {
      const numeric = Number(amount || 0);

      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(numeric, config.moneyFormat);
      }

      const value = numeric / 100;
      const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
      return `${config.currency} ${formatted}`.trim();
    }

    function absoluteUrl(path) {
      if (!path) return window.location.origin;
      if (/^https?:\/\//i.test(path)) return path;
      return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
    }

    function buildWhatsappUrl(product) {
      if (!config.whatsappNumber) return '#';

      const message = cleanText(
        `${config.whatsappText} ${product.title} ${absoluteUrl(product.url)}`
      );

      return `https://wa.me/${config.whatsappNumber}?text=${encodeURIComponent(message)}`;
    }

    function normalizePredictiveProduct(item) {
      const handle = getHandle(item);

      return {
        handle,
        url: item.url || (handle ? `/products/${handle}` : '#'),
        title: item.title || 'Product',
        vendor: item.vendor || '',
        image: normalizeImage(item.featured_image || item.image),
        price: Number(item.price_min || item.price || 0),
        variantId: null,
        available: item.available !== false,
        canAdd: false
      };
    }

    function variantHasPositiveStock(variant) {
      if (!variant || variant.available !== true) return false;
      if (Number(variant.price || 0) <= 0) return false;
      if (!variant.inventory_management) return true;

      const quantity = Number(variant.inventory_quantity);
      return Number.isFinite(quantity) ? quantity > 0 : variant.available === true;
    }

    async function hydrateProduct(item, signal) {
      const handle = getHandle(item);
      if (!handle) return normalizePredictiveProduct(item);

      try {
        const response = await fetch(`${normalizedRoot}products/${handle}.js`, { signal });
        if (!response.ok) throw new Error('Product JSON unavailable');

        const product = await response.json();
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const purchasableVariant = variants.find(variantHasPositiveStock) || null;
        const displayVariant =
          purchasableVariant ||
          variants.find((variant) => variant.available) ||
          variants[0] ||
          null;

        return {
          handle,
          url: product.url || item.url || `/products/${handle}`,
          title: product.title || item.title || 'Product',
          vendor: product.vendor || item.vendor || '',
          image: normalizeImage(
            product.featured_image ||
            product.images?.[0] ||
            item.featured_image ||
            item.image
          ),
          price: displayVariant
            ? Number(displayVariant.price || 0)
            : Number(product.price || item.price_min || item.price || 0),
          variantId: purchasableVariant?.id || null,
          available: Boolean(displayVariant?.available),
          canAdd: Boolean(purchasableVariant)
        };
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return normalizePredictiveProduct(item);
      }
    }

    function whatsappIcon() {
      return `
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20.52 3.48A11.78 11.78 0 0 0 12.14 0C5.62 0 .31 5.31.31 11.83c0 2.08.55 4.12 1.59 5.91L.22 24l6.4-1.68a11.84 11.84 0 0 0 5.52 1.4h.01c6.52 0 11.83-5.31 11.83-11.83 0-3.16-1.23-6.13-3.46-8.41ZM12.15 21.7h-.01a9.82 9.82 0 0 1-5.01-1.37l-.36-.22-3.8 1 1.01-3.7-.24-.38a9.78 9.78 0 0 1-1.5-5.2c0-5.42 4.42-9.84 9.86-9.84 2.63 0 5.11 1.03 6.97 2.89a9.78 9.78 0 0 1 2.89 6.96c.01 5.44-4.41 9.86-9.81 9.86Zm5.4-7.38c-.29-.15-1.74-.86-2.01-.96-.27-.1-.46-.15-.66.15-.19.29-.76.96-.93 1.15-.17.2-.34.22-.63.08-.29-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.18-.24-.58-.48-.5-.66-.51h-.56c-.2 0-.52.07-.79.37-.27.29-1.04 1.02-1.04 2.49 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.19 1.87.12.57-.09 1.74-.71 1.99-1.4.25-.68.25-1.27.17-1.4-.07-.12-.27-.19-.56-.34Z" fill="currentColor"/>
        </svg>
      `;
    }

    function productCard(product) {
      const placeholder = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="240" height="240">
          <rect width="100%" height="100%" fill="#f3f4f6"/>
          <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#9ca3af" font-family="Arial" font-size="18">No image</text>
        </svg>
      `);

      const image = product.image || placeholder;
      const showPrice = Number(product.price || 0) > 0;
      const canAdd = Boolean(product.canAdd && product.variantId);
      const whatsappLink = buildWhatsappUrl(product);
      const icon = whatsappIcon();

      return `
        <article class="pf-card">
          <a class="pf-image-link" href="${escapeHtml(product.url)}" aria-label="${escapeHtml(product.title)}">
            <img class="pf-image" src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">
          </a>

          <div class="pf-card-main">
            <div class="pf-card-top">
              <span class="pf-vendor">${escapeHtml(product.vendor || 'FAJ')}</span>
            </div>

            <a class="pf-product-title" href="${escapeHtml(product.url)}">
              ${escapeHtml(product.title)}
            </a>

            <div class="pf-card-bottom">
              <div class="pf-price-slot${showPrice ? '' : ' is-empty'}" aria-hidden="${showPrice ? 'false' : 'true'}">
                ${showPrice
                  ? `<span class="pf-price">${escapeHtml(formatMoney(product.price))}</span>`
                  : '<span class="pf-price">&nbsp;</span>'}
              </div>

              ${canAdd ? `
                <div class="pf-actions">
                  ${config.whatsappNumber ? `
                    <a class="pf-whatsapp-product" href="${escapeHtml(whatsappLink)}" target="_blank" rel="noopener" aria-label="${escapeHtml(config.quoteButtonText)}">
                      ${icon}
                    </a>
                  ` : ''}

                  <button type="button" class="pf-add" data-pf-add data-variant-id="${product.variantId}">
                    ${escapeHtml(config.addButtonText)}
                  </button>
                </div>
              ` : `
                <div class="pf-actions pf-actions--quote-only">
                  ${config.whatsappNumber ? `
                    <a class="pf-whatsapp-product pf-whatsapp-product--quote" href="${escapeHtml(whatsappLink)}" target="_blank" rel="noopener">
                      ${icon}
                      <span>${escapeHtml(config.quoteButtonText)}</span>
                    </a>
                  ` : ''}
                </div>
              `}
            </div>
          </div>
        </article>
      `;
    }

    async function runSearch() {
      const query = getSearchQuery();
      updateViewMore(query, Boolean(query));

      if (!query) {
        showState('Start typing a product name, model number, brand, product type, or category.');
        return;
      }

      activeController?.abort();
      activeController = new AbortController();
      showLoading();

      try {
        const url = new URL(`${normalizedRoot}search/suggest.json`, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', String(Math.min(config.limit, 10)));
        url.searchParams.set('resources[limit_scope]', 'all');
        url.searchParams.set('resources[options][unavailable_products]', 'hide');
        url.searchParams.set(
          'resources[options][fields]',
          'title,product_type,vendor,tag,variants.sku,variants.barcode,variants.title,body'
        );

        const response = await fetch(url.toString(), { signal: activeController.signal });
        if (!response.ok) throw new Error('Search request failed');

        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        const limited = products.slice(0, config.limit);

        if (!limited.length) {
          showState(`No matching products found for “${query}”. Try another product name, model number, brand, or category.`);
          return;
        }

        const hydrated = await Promise.all(
          limited.map((item) => hydrateProduct(item, activeController.signal))
        );
        const validProducts = hydrated.filter(Boolean);

        if (!validProducts.length) {
          showState(`No matching products found for “${query}”. Try another product name, model number, brand, or category.`);
          return;
        }

        hideState();
        updateCount(validProducts.length);
        grid.innerHTML = validProducts.map(productCard).join('');
      } catch (error) {
        if (error.name === 'AbortError') return;
        showState('Something went wrong while searching. Please try again.');
        console.error('[FAJ Product Finder Search]', error);
      }
    }

    async function addToCart(button) {
      const variantId = button.getAttribute('data-variant-id');
      if (!variantId || button.disabled || pendingAddButtons.has(button)) return;

      pendingAddButtons.add(button);

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';

      try {
        const response = await fetch(`${normalizedRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }]
          })
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Could not add item');
        }

        button.textContent = config.addedButtonText;

        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: data }
        }));
        document.dispatchEvent(new CustomEvent('faj:cart:updated', {
          bubbles: true,
          detail: { cart: data }
        }));
        window.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { cart: data }
        }));

        window.location.assign(`${normalizedRoot}cart`);
      } catch (error) {
        pendingAddButtons.delete(button);
        button.disabled = false;
        button.textContent = 'Try Again';
        console.error('[FAJ Product Finder Cart]', error);

        window.setTimeout(() => {
          button.textContent = originalText;
        }, 1800);
      }
    }

    function setActivePill(selectedButton) {
      pills.forEach((button) => button.classList.remove('is-active'));

      if (!selectedButton) {
        activeFilter = '';
        return;
      }

      selectedButton.classList.add('is-active');
      activeFilter = cleanText(selectedButton.getAttribute('data-pf-filter'));
    }

    input.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(runSearch, 350);
    });

    category.addEventListener('change', runSearch);
    submit.addEventListener('click', runSearch);

    pills.forEach((button) => {
      button.addEventListener('click', () => {
        setActivePill(button);
        runSearch();
      });
    });

    function onRootClick(event) {
      const addButton = event.target.closest('[data-pf-add]');
      if (!addButton || !root.contains(addButton)) return;

      event.preventDefault();
      event.stopPropagation();
      addToCart(addButton);
    }

    root.addEventListener('click', onRootClick);

    if (mainWhatsapp && config.whatsappNumber) {
      mainWhatsapp.addEventListener('click', () => {
        const query = getSearchQuery();
        const message = query
          ? `Hello, I need help finding this product: ${query}`
          : 'Hello, I need help finding a product.';

        mainWhatsapp.href =
          `https://wa.me/${config.whatsappNumber}?text=${encodeURIComponent(message)}`;
      });
    }

    if (config.showDefaultResults && cleanText(config.defaultQuery)) {
      runSearch();
    } else {
      showState('Start typing a product name, model number, brand, product type, or category.');
    }

    root.pfProductFinder = {
      destroy() {
        activeController?.abort();
        window.clearTimeout(debounceTimer);
        root.removeEventListener('click', onRootClick);
        delete root.dataset.pfInitialized;
      }
    };
  }

  function initAll(scope = document) {
    if (scope.matches?.(SELECTOR)) initProductFinder(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initProductFinder);
  }

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll();
    return;
  }

  window[GLOBAL_KEY] = {
    init: initProductFinder,
    initAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(SELECTOR)
      ? event.target
      : event.target.querySelector?.(SELECTOR);

    root?.pfProductFinder?.destroy?.();
  });
})();
